import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { supabase } from '../lib/supabase';
import { syncManager } from '../lib/syncManager';
import { dedupeInit, isCloudFresh, markCloudFresh } from '../lib/cloudBoot';
import { accountGeneration, accountStorage, cachedUserIdOwner, scopeToAccount } from '../lib/accountStorage';
import { defectsOnly } from '../utils/gpb/validateDefinition';
import { publicationFindings } from '../utils/gpb/publicationContract';
import { readDefinition } from '../utils/gpb/parseDefinition';
import { BUNDLED } from '../constants/gpb/registry';
import type { ShapeIssue } from '../utils/gpb/parseDefinition';
import type { GpbDefect } from '../utils/gpb/validateDefinition';
import type { GpbDefinition } from '../types/gpb';

/**
 * ⭐⭐ **AUTHORED PROFILES — THE ONES THAT ARE NOT IN THE BUNDLE.**
 *
 * Athlete is a TypeScript const and stays one. Builds 2–7 are written rather than
 * compiled, and live here: one shared draft per profile, and published versions
 * everybody can read.
 *
 * ⭐⭐ **PLATFORM-OWNED, ADMIN-EDITED (owner, 2026-10-01 — migration 0277).** A
 * profile belongs to Metanoia, not to the account that wrote it. Only admins save
 * drafts and publish (RLS on `is_admin()`); any admin may publish the next version
 * of any profile; and `updated_by` / `author_id` are HISTORY, cleared — never
 * cascaded — when that account is deleted. It used to be one author's private
 * property, so losing that mailbox would have erased every version people were
 * pinned to and frozen the profile forever.
 *
 * ⚠️⚠️⚠️ **`isOfficial` NEVER COMES FROM THE DEFINITION (D38).** The `definition`
 * jsonb is author-controlled and may say anything at all — `author: 'Metanoia'`,
 * or an `official: true` of its own. Neither means a thing. The answer is the
 * server column `is_official`, which no RLS policy lets anybody set, and it is
 * kept in `officialKeys` HERE, deliberately apart from the definitions, so that
 * reading a definition can never accidentally read a claim. *A declaration is a
 * field and a field can be copied; where a thing came from cannot.*
 *
 * ⚠️⚠️ **PUBLISHING IS APPEND-ONLY AND THE DATABASE ENFORCES IT.** There is no
 * update or delete grant on `gpb_profile_versions`. A taker pinned to a version
 * must find it unchanged (D37), so correcting a release means publishing another
 * — which is D23: a release, not a save.
 *
 * ⚠️ A DRAFT IS ALLOWED TO BE BROKEN. Validation gates publishing, never saving;
 * refusing to store work in progress would make the editor unusable.
 *
 * ⭐⭐ **A SAVE SAYS WHETHER IT HAPPENED (authoring workspace, 2026-10-03).** This
 * used to write the local copy FIRST, log a server refusal and return nothing — so
 * the panel said "Draft saved" over a draft that existed only in this browser, and
 * a discard removed the local row before the server had agreed. Now:
 *  - `drafts` holds what the SERVER has, and only that. Work not yet saved lives in
 *    the editor's own copy (`gpbEditorStore`), which a failure never touches.
 *  - Saves are COMPARE-AND-SET on `updated_at` (stamped by the 0272 trigger): a new
 *    draft INSERTS, so a draft another admin created meanwhile is a conflict rather
 *    than an overwrite; an existing one UPDATES only while it is still the version
 *    this editor opened. Same rule as Domain Progression's plan drafts.
 *  - Every result is returned, never logged-and-swallowed.
 */

const DRAFTS = 'gpb_profiles';

/** A copy of `rec` without `key`. */
function without<T>(rec: Record<string, T>, key: string): Record<string, T> {
  if (!(key in rec)) return rec;
  const next = { ...rec };
  delete next[key];
  return next;
}

const errorText = (e: unknown): string =>
  e instanceof Error ? e.message : typeof e === 'object' && e && 'message' in e ? String((e as { message: unknown }).message) : String(e);
const VERSIONS = 'gpb_profile_versions';

/** `${definitionId}@${version}` — the key both maps share. */
export const versionKey = (definitionId: string, version: string): string =>
  `${definitionId}@${version}`;

interface DraftRow { definition_id: string; draft: unknown; updated_at: string; updated_by: string | null }

/** What the server last said about a draft — the compare-and-set base. */
export interface DraftMeta { updatedAt: string; updatedBy: string | null }

export interface DraftSaveResult {
  ok: boolean;
  /** The server's `updated_at` after a successful save — the next save's base. */
  updatedAt?: string;
  /** Someone else saved (or created, or removed) this draft since it was opened. */
  conflict?: boolean;
  error?: string;
}
interface VersionRow {
  /** Who published it — history, not ownership. Null once that account is gone. */
  definition_id: string; version: string; author_id: string | null;
  definition: GpbDefinition; is_official: boolean; published_at: string;
}

export interface PublishResult {
  ok: boolean;
  /** Why it was refused. Empty when `ok`. */
  defects: readonly GpbDefect[];
  /** A database refusal — a duplicate version, or an account that is not an admin. */
  error?: string;
}

interface CatalogState {
  /**
   * Published definitions, `id -> version -> definition`.
   * ⚠️ Same shape as the registry's bundled map, so the resolver can merge the two
   * without either side knowing which it is looking at.
   */
  published: Record<string, Record<string, GpbDefinition>>;
  /**
   * ⚠️⚠️ Publication ORDER per id, oldest first — never a string sort. `'0.10'`
   * sorts before `'0.9'`, so sorting would quietly make the newest release the
   * oldest, which is the bug D37 calls out by name.
   */
  order: Record<string, readonly string[]>;
  /** ⚠️ Server-granted official versions, by `versionKey`. Never from a payload. */
  officialKeys: readonly string[];
  /**
   * ⚠️⚠️ **UNPROMOTED VERSIONS THIS VIEWER MAY SEE**, by `versionKey`: EVERY
   * published version for an admin, none for anybody else. Catalogue membership
   * is *bundled + official + these*: the editors must be able to see and take a
   * profile before it is promoted, and nobody else should until it is.
   *
   * ⚠️ The NAME is historical (it meant "versions I authored" when profiles had
   * one author) and is kept because it is a PERSISTED key. Admin-ness comes from
   * the server (`is_admin()`), never from the definition's `author` display name —
   * a string anybody can type.
   */
  mineKeys: readonly string[];
  /** The shared drafts (admins only — RLS returns none to anybody else), by id.
   *  ⚠️ SERVER copies only — never a local edit that has not been saved. */
  drafts: Record<string, GpbDefinition>;
  draftMeta: Record<string, DraftMeta>;
  /**
   * ⚠️ Stored drafts whose SHAPE is wrong (a list that is an object, a count that is
   * text). They cannot be opened in the visual editor without crashing it or
   * misreading it, so they are listed with their issues and opened as JSON only.
   */
  malformedDrafts: Record<string, { raw: unknown; issues: readonly ShapeIssue[]; meta: DraftMeta }>;
  /**
   * ⚠️⚠️ Published rows REFUSED by this client: a payload whose own id or version
   * is not the row's, or whose shape is wrong. Ignored rather than resolved — a row
   * filed under one id carrying another profile would otherwise be served as it.
   * Kept, by `versionKey`, so an admin can see that something was refused.
   */
  ignoredVersions: readonly string[];
  /** Cosmetic — real gates are RLS and admin-ops. From `is_admin()`, fails closed. */
  isEditor: boolean;
  loadStatus: 'idle' | 'loading' | 'ready' | 'error';
  loadError: string | null;

  initializedUserId: string | null;
  cachedUserId: string | null;

  initialize: () => Promise<void>;
  /** Re-read everything from the server — what "Reload theirs" after a conflict does. */
  refresh: () => Promise<void>;
  /**
   * @param baseUpdatedAt the draft's `updated_at` when the editor opened it; `null`
   *   = a new draft (INSERT); omitted = the last one this store saw.
   */
  saveDraft: (def: GpbDefinition, baseUpdatedAt?: string | null) => Promise<DraftSaveResult>;
  /** ⚠️ The SHARED draft — gone for every admin. Refused if it changed since `baseUpdatedAt`. */
  discardDraft: (definitionId: string, baseUpdatedAt?: string) => Promise<DraftSaveResult>;
  /** ⚠️ Refuses defects and contract defects. Gaps do not block — Athlete has nine. */
  publish: (def: GpbDefinition) => Promise<PublishResult>;
}

export const useGpbCatalogStore = create<CatalogState>()(
  persist(
    (set, get) => ({
      published: {},
      order: {},
      officialKeys: [],
      mineKeys: [],
      drafts: {},
      draftMeta: {},
      malformedDrafts: {},
      ignoredVersions: [],
      isEditor: false,
      loadStatus: 'idle',
      loadError: null,
      initializedUserId: null,
      cachedUserId: null,

      initialize: () => dedupeInit('gpbCatalog', async () => {
        const at = accountGeneration();
        try {
          const { user } = await syncManager.safeAuthCheck();
          if (!user) return;
          if (get().initializedUserId === user.id) return;
          if (get().cachedUserId === user.id && isCloudFresh('gpbCatalog', user.id)) {
            set({ initializedUserId: user.id });
            return;
          }

          /* ⚠️ Published versions are everybody's; drafts are the admins', and
             RLS already returns none to anybody else without a filter here. */
          set({ loadStatus: 'loading', loadError: null });
          const [versions, drafts, admin] = await Promise.all([
            supabase.from(VERSIONS)
              .select('definition_id, version, author_id, definition, is_official, published_at')
              .order('published_at', { ascending: true }),
            supabase.from(DRAFTS).select('definition_id, draft, updated_at, updated_by'),
            supabase.rpc('is_admin'),
          ]);
          /* ⚠️⚠️ An answer for an account that has since signed out is not this copy's — an
             admin's drafts and unpromoted versions would be the next account's to see. */
          if (at !== accountGeneration()) return;
          if (versions.error) throw versions.error;
          if (drafts.error) throw drafts.error;
          /* ⚠️ Fails CLOSED: an error or anything but a literal `true` is "not an
             editor", which only hides unpromoted versions — never shows them. */
          const isEditor = !admin.error && admin.data === true;

          const published: Record<string, Record<string, GpbDefinition>> = {};
          const order: Record<string, string[]> = {};
          const officialKeys: string[] = [];
          const mineKeys: string[] = [];
          const ignoredVersions: string[] = [];

          for (const row of (versions.data ?? []) as VersionRow[]) {
            /* ⚠️⚠️ THE ROW IS THE AUTHORITY FOR WHICH PROFILE AND WHICH VERSION; the
               payload must agree with it or the row is not served. Otherwise a row
               filed as `scholar@0.1` carrying `{ id: 'athlete-home' }` would be read
               — by every surface keyed on `def.id` — as Athlete. */
            const read = readDefinition(row.definition);
            if (!read.ok || read.def.id !== row.definition_id || read.def.version !== row.version) {
              ignoredVersions.push(versionKey(row.definition_id, row.version));
              continue;
            }
            (published[row.definition_id] ??= {})[row.version] = read.def;
            (order[row.definition_id] ??= []).push(row.version);
            const key = versionKey(row.definition_id, row.version);
            /* ⚠️⚠️ THE ONLY PLACE OFFICIALNESS IS READ, and it is the COLUMN. */
            if (row.is_official) officialKeys.push(key);
            if (isEditor) mineKeys.push(key);
          }

          /* ⚠️⚠️ DRAFTS ARE REPLACED, NEVER MERGED WITH THE CACHE. The old merge kept
             any local draft the server did not have — which is exactly a save that
             FAILED, then shown as saved. Unsaved work is the editor copy's job. */
          const nextDrafts: Record<string, GpbDefinition> = {};
          const draftMeta: Record<string, DraftMeta> = {};
          const malformedDrafts: CatalogState['malformedDrafts'] = {};
          for (const row of (drafts.data ?? []) as DraftRow[]) {
            const meta = { updatedAt: row.updated_at, updatedBy: row.updated_by ?? null };
            const read = readDefinition(row.draft);
            if (read.ok) { nextDrafts[row.definition_id] = read.def; draftMeta[row.definition_id] = meta; }
            else malformedDrafts[row.definition_id] = { raw: row.draft, issues: read.issues, meta };
          }

          set({
            published, order, officialKeys, mineKeys, ignoredVersions, isEditor,
            drafts: nextDrafts, draftMeta, malformedDrafts,
            loadStatus: 'ready', loadError: null,
            initializedUserId: user.id, cachedUserId: user.id,
          });
          markCloudFresh('gpbCatalog', user.id);
        } catch (error) {
          console.error('Failed to load the Growth Profile catalogue:', error);
          set({ loadStatus: 'error', loadError: errorText(error) });
        }
      }),

      refresh: async () => {
        set({ initializedUserId: null });
        await get().initialize();
      },

      saveDraft: async (def, baseUpdatedAt) => {
        try {
          const { user } = await syncManager.safeAuthCheck();
          if (!user) return { ok: false, error: 'You are signed out — nothing was saved. Your edits are kept in this browser.' };
          if (!def.id?.trim()) return { ok: false, error: 'The profile needs an id before it can be saved.' };
          const base = baseUpdatedAt === undefined ? get().draftMeta[def.id]?.updatedAt ?? null : baseUpdatedAt;
          /* ⭐ ONE draft per profile, shared by the admins: keyed by the profile id
             alone, with `updated_by` as history (0277). */
          const row = { definition_id: def.id, updated_by: user.id, draft: def };
          const res = base === null
            ? await supabase.from(DRAFTS).insert(row).select('updated_at').maybeSingle()
            : await supabase.from(DRAFTS).update(row).eq('definition_id', def.id).eq('updated_at', base)
              .select('updated_at').maybeSingle();
          if (res.error) {
            if ((res.error as { code?: string }).code === '23505') {
              return { ok: false, conflict: true,
                error: 'Another admin created a draft for this profile meanwhile. Reload to see it before saving yours.' };
            }
            return { ok: false, error: res.error.message };
          }
          if (!res.data) {
            return { ok: false, conflict: true,
              error: 'This draft was saved or removed by someone else since you opened it. Reload to see the current version before saving yours.' };
          }
          const updatedAt = (res.data as { updated_at: string }).updated_at;
          set(state => ({
            drafts: { ...state.drafts, [def.id]: def },
            draftMeta: { ...state.draftMeta, [def.id]: { updatedAt, updatedBy: user.id } },
            /* A malformed draft saved from the editor is a sound one now. */
            malformedDrafts: without(state.malformedDrafts, def.id),
          }));
          return { ok: true, updatedAt };
        } catch (error) {
          return { ok: false, error: errorText(error) };
        }
      },

      discardDraft: async (definitionId, baseUpdatedAt) => {
        try {
          const { user } = await syncManager.safeAuthCheck();
          if (!user) return { ok: false, error: 'You are signed out — nothing was removed.' };
          const base = baseUpdatedAt
            ?? get().draftMeta[definitionId]?.updatedAt
            ?? get().malformedDrafts[definitionId]?.meta.updatedAt;
          // ⚠️ The shared draft — discarding it discards it for every admin, so only
          // the version this admin was looking at may be discarded.
          let q = supabase.from(DRAFTS).delete().eq('definition_id', definitionId);
          if (base) q = q.eq('updated_at', base);
          const res = await q.select('definition_id');
          if (res.error) return { ok: false, error: res.error.message };
          if (!res.data || (res.data as unknown[]).length === 0) {
            return { ok: false, conflict: true,
              error: 'The draft changed or was already removed since you opened it. Reload before discarding.' };
          }
          set(state => ({
            drafts: without(state.drafts, definitionId),
            draftMeta: without(state.draftMeta, definitionId),
            malformedDrafts: without(state.malformedDrafts, definitionId),
          }));
          return { ok: true };
        } catch (error) {
          return { ok: false, error: errorText(error) };
        }
      },

      /**
       * ⚠️⚠️ **THE ONE GATE.** A draft may be broken; a RELEASE may not, because
       * takers are pinned to it and it can never be edited afterwards. Defects
       * block; gaps do not — Athlete ships with nine and is the profile the whole
       * engine was built against.
       */
      publish: async def => {
        /* ⭐ The engine's rules AND the product contract (seven ranks, the seven-tier
           ability). Gaps still never block. */
        /* ⚠️⚠️ THE RELEASE LINE INCLUDES THE BUNDLE. A revision of Athlete supersedes
           the BUNDLED 0.1, which the database has never seen — checking `supersedes`
           against the table alone would call it "never published", and a version
           number the bundle already ships would slip past the table's unique key
           and shadow a release people are pinned to. */
        const released = [...Object.keys(BUNDLED[def.id] ?? {}), ...(get().order[def.id] ?? [])];
        const found = publicationFindings(def, released);
        const defects = [...defectsOnly(found)];
        if (released.includes(def.version)) {
          defects.push({ code: 'release-mismatch', severity: 'defect', where: 'release',
            message: `v${def.version} is already published — a release is never replaced; publish the next number.` });
        }
        if (defects.length > 0) return { ok: false, defects };

        try {
          const { user } = await syncManager.safeAuthCheck();
          if (!user) return { ok: false, defects: [], error: 'Not signed in.' };

          /* ⚠️ `is_official` is NOT sent. It defaults to false and the insert
             policy REQUIRES false (0277) — officialness is granted in the console
             (D38). `author_id` is who published, not who owns. */
          const { error } = await supabase.from(VERSIONS).insert({
            definition_id: def.id, version: def.version,
            author_id: user.id, definition: def,
          });
          if (error) return { ok: false, defects: [], error: error.message };

          set(state => ({
            published: {
              ...state.published,
              [def.id]: { ...(state.published[def.id] ?? {}), [def.version]: def },
            },
            order: {
              ...state.order,
              [def.id]: [...(state.order[def.id] ?? []), def.version],
            },
            /* ⚠️ Visible to this editor immediately, without waiting for a reload.
               Official it is NOT — that is granted in the console, and this insert
               could not set it if it tried. */
            mineKeys: [...state.mineKeys, versionKey(def.id, def.version)],
          }));
          return { ok: true, defects: [] };
        } catch (error) {
          return { ok: false, defects: [], error: String(error) };
        }
      },
    }),
    {
      name: 'metanoia-gpb-catalog',
      // ⭐ One saved copy per account — see `scopeToAccount` below.
      storage: accountStorage(cachedUserIdOwner),
      /* ⚠️⚠️ v1 (2026-10-03): the v0 cache could hold drafts that NEVER reached the
         server — the old save wrote locally first and swallowed the refusal. Those
         are dropped here rather than shown as saved; the server copy returns on the
         next load. Published versions and keys are kept (they came from the server). */
      version: 1,
      migrate: (persisted, from) => {
        const state = (persisted ?? {}) as Record<string, unknown>;
        if (from < 1) return { ...state, drafts: {}, draftMeta: {} } as unknown as CatalogState;
        return state as unknown as CatalogState;
      },
      /* ⚠️⚠️ `officialKeys` IS CACHED BUT NEVER AUTHORITATIVE ON ITS OWN — it is
         replaced wholesale on every load from the server column. Persisting it
         keeps the badge stable offline; it cannot grant one, because nothing
         writes to it except `initialize`. */
      partialize: state => ({
        published: state.published,
        order: state.order,
        officialKeys: state.officialKeys,
        mineKeys: state.mineKeys,
        drafts: state.drafts,
        draftMeta: state.draftMeta,
        cachedUserId: state.cachedUserId,
      }),
    },
  ),
);

/* ⚠️⚠️ ONE SAVED COPY PER ACCOUNT (lib/accountStorage). What this store holds is what ONE viewer may
   see: an admin's copy has the shared drafts and every unpromoted version (`mineKeys`). The device kept
   one copy and a sign-out does not reload the page, so the next account — any account — was shown an
   admin's drafts and could take an unpromoted profile until its own load replaced them. */
scopeToAccount(useGpbCatalogStore);
